import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Avatar,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  GetApp as DownloadIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Psychology as AIIcon,
  CheckCircle,
  TrendingUp,
  Warning,
  Star,
  ExpandMore,
  Help as QuestionIcon,
  Lightbulb as RecommendationIcon,
  Code as CodeIcon,
  BusinessCenter as ExperienceIcon,
  Schedule as ScheduleIcon,
  SmartToy as SmartToyIcon,
  History as HistoryIcon,
  Upload as UploadIcon,
  SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { getResumeUrl } from '../../services/storageService';
import { parseResumeWithAI, matchResumeWithJD, isAIEnabled, AIMatchingResult } from '../../services/aiResumeParserService';
import { initiateAICall, formatPhoneNumber, fetchAndSaveCallStatus } from '../../services/elevenLabsService';
import { useToast, LoadingButton } from '../../components/ui';
import { NotesChat } from '../../components/NotesChat';
import { getApplicationNotes, ApplicationNote } from '../../services/applicationService';

interface ApplicationData {
  id: string;
  jobId: string;
  jobTitle: string;
  jobDescription?: string;
  applicantName: string;
  applicantEmail: string;
  applicantPhone?: string;
  resumeUrl: string;
  coverLetter?: string;
  status: 'pending' | 'reviewing' | 'shortlisted' | 'rejected' | 'hired';
  matchScore?: number;
  screeningResults?: any;
  aiAnalysisResults?: AIMatchingResult;
  submittedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  notes?: string;
}

export const ApplicationDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showSuccess, showError } = useToast();

  const [application, setApplication] = useState<ApplicationData | null>(null);
  const [jobDetails, setJobDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCallLoading, setAiCallLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [auditTrail, setAuditTrail] = useState<ApplicationNote[]>([]);

  const loadCurrentUser = useCallback(async () => {
    const user = await getAuthUser();
    setCurrentUser(user);
  }, []);

  const loadApplication = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const user = await getAuthUser();
      if (!user) {
        setError('You must be logged in');
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('applications')
        .select(`
          *,
          job:jobs!applications_job_id_fkey(id, title, description, requirements, benefits, location),
          reviewer:users!applications_reviewed_by_fkey(name)
        `)
        .eq('id', id)
        .eq('entity_id', user.entityId)
        .single();

      if (fetchError) throw fetchError;

      if (!data) {
        setError('Application not found');
        return;
      }

      setApplication({
        id: data.id,
        jobId: data.job_id,
        jobTitle: data.job?.title || 'Unknown Job',
        jobDescription: data.job?.description,
        applicantName: data.applicant_name,
        applicantEmail: data.applicant_email,
        applicantPhone: data.applicant_phone,
        resumeUrl: data.resume_url,
        coverLetter: data.cover_letter,
        status: data.status,
        matchScore: data.match_score,
        screeningResults: data.screening_results,
        aiAnalysisResults: data.ai_analysis_results,
        submittedAt: data.submitted_at,
        reviewedBy: data.reviewer?.name,
        reviewedAt: data.reviewed_at,
        notes: data.notes
      });

      setJobDetails(data.job);
    } catch (err: any) {
      console.error('Error loading application:', err);
      setError(err.message || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAuditTrail = useCallback(async () => {
    if (!id) return;
    const notes = await getApplicationNotes(id);
    setAuditTrail(notes.filter(n => n.note_type === 'status_change'));
  }, [id]);

  useEffect(() => {
    if (id) {
      loadApplication();
      loadCurrentUser();
      loadAuditTrail();
    }
  }, [id, loadApplication, loadCurrentUser, loadAuditTrail]);

  const handleDownloadResume = async () => {
    if (!application) return;
    try {
      if (application.resumeUrl.startsWith('bulk-upload/')) {
        showError('Resume file not available for download. This application was created via bulk upload.');
        return;
      }

      const url = await getResumeUrl(application.resumeUrl);
      if (!url) {
        showError('Resume file not available');
        return;
      }
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading resume:', err);
      showError('Failed to download resume');
    }
  };

  const handleAIAnalysis = async () => {
    if (!application || !jobDetails) {
      showError('Missing application or job details');
      return;
    }

    if (!isAIEnabled()) {
      showError('AI analysis is not enabled');
      return;
    }

    setAiLoading(true);
    setError('');

    try {
      const resumeUrl = await getResumeUrl(application.resumeUrl);
      if (!resumeUrl) {
        showError('Resume file not available');
        setAiLoading(false);
        return;
      }

      const resumeResponse = await fetch(resumeUrl);
      const resumeBlob = await resumeResponse.blob();
      const resumeFile = new File([resumeBlob], 'resume.pdf', { type: resumeBlob.type });

      const parsedResume = await parseResumeWithAI(resumeFile);
      const matchResult = await matchResumeWithJD(parsedResume, {
        title: jobDetails.title,
        description: jobDetails.description,
        requirements: jobDetails.requirements || [],
        benefits: jobDetails.benefits || [],
        location: jobDetails.location,
        workType: jobDetails.work_type
      });

      // Save results
      await supabase
        .from('applications')
        .update({
          ai_analysis_results: matchResult,
          match_score: matchResult.overallScore,
          updated_at: new Date().toISOString()
        })
        .eq('id', application.id);

      setApplication(prev => prev ? {
        ...prev,
        aiAnalysisResults: matchResult,
        matchScore: matchResult.overallScore
      } : null);

      showSuccess('AI analysis completed successfully!');
    } catch (err: any) {
      console.error('AI analysis error:', err);
      setError(err.message || 'Failed to analyze resume');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAICall = async () => {
    if (!application || !application.applicantPhone || !currentUser) return;

    setAiCallLoading(true);
    try {
      const result = await initiateAICall({
        applicationId: application.id,
        applicantName: application.applicantName,
        applicantPhone: application.applicantPhone,
        jobTitle: application.jobTitle,
        matchScore: application.matchScore,
        entityId: currentUser.entityId,
      });

      if (result.success && result.callId) {
        showSuccess(`AI call initiated to ${formatPhoneNumber(application.applicantPhone)}`);
        setTimeout(async () => {
          await fetchAndSaveCallStatus(result.callId!, application.id, currentUser.entityId);
        }, 10000);
      } else {
        showError(result.error || 'Failed to initiate AI call');
      }
    } catch (err: any) {
      showError('An unexpected error occurred');
    } finally {
      setAiCallLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; color: any }> = {
      pending: { label: 'New', color: 'warning' },
      reviewing: { label: 'Under Review', color: 'info' },
      shortlisted: { label: 'Shortlisted', color: 'success' },
      rejected: { label: 'Rejected', color: 'error' },
      hired: { label: 'Hired', color: 'success' }
    };
    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} />;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Extract parsed resume data from screening results
  const getParsedResume = () => {
    if (!application?.screeningResults) return null;
    return application.screeningResults.parsedResume || null;
  };

  // Get overall score from various sources
  const getOverallScore = () => {
    if (application?.matchScore !== undefined) return application.matchScore;
    if (application?.aiAnalysisResults?.overallScore !== undefined) return application.aiAnalysisResults.overallScore;
    if (application?.screeningResults?.matchResult?.overallScore !== undefined) return application.screeningResults.matchResult.overallScore;
    if (application?.screeningResults?.overallScore !== undefined) return application.screeningResults.overallScore;
    return null;
  };

  // Get AI analysis results
  const getAIAnalysis = (): AIMatchingResult | null => {
    if (application?.aiAnalysisResults) return application.aiAnalysisResults;
    if (application?.screeningResults?.matchResult) return application.screeningResults.matchResult;
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !application) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/applications')} sx={{ mt: 2 }}>
          Back to Applications
        </Button>
      </Container>
    );
  }

  if (!application) return null;

  const parsedResume = getParsedResume();
  const overallScore = getOverallScore();
  const aiAnalysis = getAIAnalysis();

  return (
    <Box sx={{ py: 4, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/admin/applications')}>
                <BackIcon />
              </IconButton>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                {getInitials(application.applicantName)}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {application.applicantName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Applied for: {application.jobTitle}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              {getStatusChip(application.status)}
              {overallScore !== null && (
                <Chip
                  label={`Match: ${overallScore}%`}
                  sx={{
                    bgcolor: alpha(getScoreColor(overallScore), 0.1),
                    color: getScoreColor(overallScore),
                    fontWeight: 700,
                    fontSize: '1rem',
                    py: 2
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Contact & Basic Info */}
          <Grid item xs={12} md={4}>
            {/* Contact Information */}
            <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Contact Information
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <EmailIcon color="action" />
                    <Typography variant="body2">{application.applicantEmail}</Typography>
                  </Stack>
                  {application.applicantPhone && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <PhoneIcon color="action" />
                      <Typography variant="body2">{application.applicantPhone}</Typography>
                    </Stack>
                  )}
                  <Stack direction="row" spacing={2} alignItems="center">
                    <ScheduleIcon color="action" />
                    <Typography variant="body2">
                      Applied: {new Date(application.submittedAt).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadResume}
                  >
                    Download Resume
                  </Button>

                  {application.applicantPhone && (
                    <LoadingButton
                      fullWidth
                      variant="outlined"
                      color="primary"
                      loading={aiCallLoading}
                      loadingText="Calling..."
                      startIcon={<SmartToyIcon />}
                      onClick={handleAICall}
                    >
                      AI Call
                    </LoadingButton>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Parsed Resume Summary */}
            {parsedResume && (
              <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Candidate Profile
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={2}>
                    {parsedResume.yearsOfExperience !== undefined && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Years of Experience</Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {parsedResume.yearsOfExperience} years
                        </Typography>
                      </Box>
                    )}

                    {parsedResume.careerLevel && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Career Level</Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                          {parsedResume.careerLevel}
                        </Typography>
                      </Box>
                    )}

                    {parsedResume.location && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                        <Typography variant="body1">{parsedResume.location}</Typography>
                      </Box>
                    )}

                    {parsedResume.aiSummary && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">AI Summary</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {parsedResume.aiSummary}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Skills Section */}
            {(parsedResume?.skills?.length > 0 || application.screeningResults?.skills?.length > 0) && (
              <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Skills
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {(parsedResume?.skills || application.screeningResults?.skills || []).map((skill: string, index: number) => (
                      <Chip key={index} label={skill} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Tech Stack */}
            {parsedResume?.techStack?.length > 0 && (
              <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Tech Stack
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {parsedResume.techStack.map((tech: string, index: number) => (
                      <Chip key={index} label={tech} size="small" color="primary" variant="outlined" />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Right Column - AI Analysis & Details */}
          <Grid item xs={12} md={8}>
            {/* AI Analysis Section */}
            <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    AI Analysis
                  </Typography>
                  {!aiAnalysis && isAIEnabled() && (
                    <LoadingButton
                      variant="contained"
                      startIcon={<AIIcon />}
                      loading={aiLoading}
                      loadingText="Analyzing..."
                      onClick={handleAIAnalysis}
                    >
                      Analyze with AI
                    </LoadingButton>
                  )}
                  {aiAnalysis && isAIEnabled() && (
                    <LoadingButton
                      variant="outlined"
                      startIcon={<AIIcon />}
                      loading={aiLoading}
                      loadingText="Re-analyzing..."
                      onClick={handleAIAnalysis}
                    >
                      Re-analyze
                    </LoadingButton>
                  )}
                </Stack>

                <Divider sx={{ my: 2 }} />

                {aiLoading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography>AI is analyzing the resume...</Typography>
                  </Box>
                ) : aiAnalysis ? (
                  <Box>
                    {/* Score Overview */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        mb: 3,
                        border: `2px solid ${alpha(getScoreColor(aiAnalysis.overallScore), 0.3)}`,
                        background: `linear-gradient(135deg, ${alpha(getScoreColor(aiAnalysis.overallScore), 0.08)} 0%, transparent 100%)`,
                        borderRadius: 2
                      }}
                    >
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box
                              sx={{
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `conic-gradient(${getScoreColor(aiAnalysis.overallScore)} ${aiAnalysis.overallScore * 3.6}deg, ${alpha(getScoreColor(aiAnalysis.overallScore), 0.2)} 0deg)`,
                                mx: 'auto'
                              }}
                            >
                              <Box
                                sx={{
                                  width: 100,
                                  height: 100,
                                  borderRadius: '50%',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography variant="h3" fontWeight={800} color={getScoreColor(aiAnalysis.overallScore)}>
                                  {aiAnalysis.overallScore}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Overall Match Score
                            </Typography>
                            {aiAnalysis.confidence && (
                              <Chip
                                label={`${aiAnalysis.confidence} confidence`}
                                size="small"
                                color={aiAnalysis.confidence === 'high' ? 'success' : aiAnalysis.confidence === 'medium' ? 'warning' : 'error'}
                                sx={{ mt: 1 }}
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <Stack spacing={2}>
                            {/* Skills Match */}
                            <Box>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography variant="body2">Skills Match</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {aiAnalysis.skillsMatch?.score || 0}%
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={aiAnalysis.skillsMatch?.score || 0}
                                sx={{ height: 8, borderRadius: 4 }}
                                color={aiAnalysis.skillsMatch?.score >= 70 ? 'success' : aiAnalysis.skillsMatch?.score >= 50 ? 'warning' : 'error'}
                              />
                            </Box>

                            {/* Experience Match */}
                            <Box>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography variant="body2">Experience Match</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {aiAnalysis.experienceMatch?.score || 0}%
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={aiAnalysis.experienceMatch?.score || 0}
                                sx={{ height: 8, borderRadius: 4 }}
                                color={aiAnalysis.experienceMatch?.score >= 70 ? 'success' : aiAnalysis.experienceMatch?.score >= 50 ? 'warning' : 'error'}
                              />
                            </Box>

                            {/* Education Match */}
                            <Box>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography variant="body2">Education Match</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {aiAnalysis.educationMatch?.score || 0}%
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={aiAnalysis.educationMatch?.score || 0}
                                sx={{ height: 8, borderRadius: 4 }}
                                color={aiAnalysis.educationMatch?.score >= 70 ? 'success' : aiAnalysis.educationMatch?.score >= 50 ? 'warning' : 'error'}
                              />
                            </Box>

                            {/* Culture Fit */}
                            {aiAnalysis.cultureFit && (
                              <Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">Culture Fit</Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    {aiAnalysis.cultureFit.score}%
                                  </Typography>
                                </Stack>
                                <LinearProgress
                                  variant="determinate"
                                  value={aiAnalysis.cultureFit.score}
                                  sx={{ height: 8, borderRadius: 4 }}
                                  color={aiAnalysis.cultureFit.score >= 70 ? 'success' : aiAnalysis.cultureFit.score >= 50 ? 'warning' : 'error'}
                                />
                              </Box>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Summary */}
                    {aiAnalysis.summary && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Summary
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {aiAnalysis.summary}
                        </Typography>
                      </Box>
                    )}

                    {/* Skills Breakdown */}
                    <Accordion defaultExpanded elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TrendingUp color="primary" />
                          <Typography fontWeight={600}>Skills Analysis</Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {/* Matched Skills */}
                          {aiAnalysis.skillsMatch?.matchedSkills?.length > 0 && (
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                Matched Skills
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                {aiAnalysis.skillsMatch.matchedSkills.map((skill, i) => (
                                  <Chip
                                    key={i}
                                    label={skill}
                                    size="small"
                                    sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark' }}
                                  />
                                ))}
                              </Stack>
                            </Grid>
                          )}

                          {/* Missing Skills */}
                          {aiAnalysis.skillsMatch?.missingSkills?.length > 0 && (
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                Missing Skills
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                {aiAnalysis.skillsMatch.missingSkills.map((skill, i) => (
                                  <Chip
                                    key={i}
                                    label={skill}
                                    size="small"
                                    sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.dark' }}
                                  />
                                ))}
                              </Stack>
                            </Grid>
                          )}

                          {/* Additional Skills */}
                          {aiAnalysis.skillsMatch?.additionalSkills?.length > 0 && (
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                Additional Skills (Bonus)
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                {aiAnalysis.skillsMatch.additionalSkills.map((skill, i) => (
                                  <Chip
                                    key={i}
                                    label={skill}
                                    size="small"
                                    sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.dark' }}
                                  />
                                ))}
                              </Stack>
                            </Grid>
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>

                    {/* Strengths */}
                    {aiAnalysis.strengths?.length > 0 && (
                      <Accordion elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CheckCircle color="success" />
                            <Typography fontWeight={600}>Strengths ({aiAnalysis.strengths.length})</Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {aiAnalysis.strengths.map((strength, i) => (
                              <ListItem key={i}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Star sx={{ color: 'success.main', fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText primary={strength} />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Concerns */}
                    {aiAnalysis.concerns?.length > 0 && (
                      <Accordion elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Warning color="warning" />
                            <Typography fontWeight={600}>Concerns ({aiAnalysis.concerns.length})</Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {aiAnalysis.concerns.map((concern, i) => (
                              <ListItem key={i}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Warning sx={{ color: 'warning.main', fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText primary={concern} />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Interview Questions */}
                    {aiAnalysis.interviewQuestions && aiAnalysis.interviewQuestions.length > 0 && (
                      <Accordion elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <QuestionIcon color="secondary" />
                            <Typography fontWeight={600}>
                              Suggested Interview Questions ({aiAnalysis.interviewQuestions.length})
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={2}>
                            {aiAnalysis.interviewQuestions.map((question, i) => (
                              <Paper
                                key={i}
                                elevation={0}
                                sx={{
                                  p: 2,
                                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                                }}
                              >
                                <Typography variant="body2">
                                  <strong>{i + 1}.</strong> {question}
                                </Typography>
                              </Paper>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Recommendations */}
                    {aiAnalysis.recommendations?.length > 0 && (
                      <Accordion elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <RecommendationIcon color="info" />
                            <Typography fontWeight={600}>Recommendations</Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {aiAnalysis.recommendations.map((rec, i) => (
                              <ListItem key={i}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <RecommendationIcon sx={{ color: 'info.main', fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText primary={rec} />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AIIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No AI analysis available yet. Click "Analyze with AI" to get detailed insights.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Education Section */}
            {parsedResume?.education?.length > 0 && (
              <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Education
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1.5}>
                    {parsedResume.education.map((edu: any, index: number) => (
                      <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: 1, flexShrink: 0 }} />
                        {typeof edu === 'string' ? (
                          <Typography variant="body1">{edu}</Typography>
                        ) : (
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {edu.degree || edu.qualification}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {edu.institution || edu.school}
                            </Typography>
                            {edu.year && (
                              <Typography variant="caption" color="text.secondary">
                                {edu.year}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Experience Section */}
            {parsedResume?.experience?.length > 0 && (
              <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    <ExperienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Experience
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1.5}>
                    {parsedResume.experience.map((exp: any, index: number) => (
                      <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: 1, flexShrink: 0 }} />
                        {typeof exp === 'string' ? (
                          <Typography variant="body1">{exp}</Typography>
                        ) : (
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {exp.title || exp.position}
                            </Typography>
                            <Typography variant="body2" color="primary.main">
                              {exp.company || exp.organization}
                            </Typography>
                            {exp.duration && (
                              <Typography variant="caption" color="text.secondary">
                                {exp.duration}
                              </Typography>
                            )}
                            {exp.description && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {exp.description}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Cover Letter */}
            {application.coverLetter && (
              <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Cover Letter
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {application.coverLetter}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Audit Trail */}
            <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <HistoryIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Audit Trail
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ position: 'relative', pl: 3 }}>
                  {/* Vertical timeline line */}
                  <Box sx={{
                    position: 'absolute',
                    left: 10,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    bgcolor: 'divider',
                  }} />

                  {/* Resume Uploaded */}
                  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5, position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      left: -19,
                      top: 4,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <UploadIcon sx={{ fontSize: 12, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Resume Uploaded
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(application.submittedAt).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        By: {application.applicantName} (Applicant)
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Status Changes */}
                  {auditTrail.map((note) => (
                    <Stack key={note.id} direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5, position: 'relative' }}>
                      <Box sx={{
                        position: 'absolute',
                        left: -19,
                        top: 4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <SwapHorizIcon sx={{ fontSize: 12, color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {note.content}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(note.created_at).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          By: {note.author_name}{note.author_role ? ` (${note.author_role})` : ''}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}

                  {auditTrail.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      No status changes recorded yet.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Notes & Activity */}
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                {currentUser && (
                  <NotesChat
                    applicationId={application.id}
                    entityId={currentUser.entityId}
                    currentUserId={currentUser.id}
                    currentUserName={currentUser.name || currentUser.email}
                    currentUserRole={currentUser.role || 'user'}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
